<script setup lang="ts">
import { reactive, ref, watch } from 'vue'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { createReferenceItem } from '../productService'
import type { ColorReference, EquipmentReference } from '../types'

const props = defineProps<{
  open: boolean
  title: string
  type: 'color' | 'equipment'
  baseAmountLabel?: string
}>()

const emit = defineEmits<{
  close: []
  created: [item: ColorReference | EquipmentReference]
}>()

const client = useApiClient()
const saving = ref(false)
const errorMessage = ref('')
const form = reactive({
  code: '',
  name: '',
  base_amount: '',
})

function reset(): void {
  form.code = ''
  form.name = ''
  form.base_amount = ''
  errorMessage.value = ''
}

watch(() => props.open, (open) => {
  if (open) reset()
}, { immediate: true })

async function submit(): Promise<void> {
  saving.value = true
  errorMessage.value = ''
  try {
    const item = await createReferenceItem(client, props.type, {
      code: form.code.trim(),
      name: form.name.trim(),
      ...(form.base_amount.trim()
        ? { base_amount: Number(form.base_amount) }
        : {}),
      is_active: 1,
    })
    emit('created', item as ColorReference | EquipmentReference)
  } catch (error: unknown) {
    errorMessage.value = formatApiError(error)
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="modal-backdrop" @click.self="emit('close')">
      <section class="create-modal" role="dialog" aria-modal="true" :aria-label="title">
        <header>
          <div>
            <p class="eyebrow">Repository tool</p>
            <h3>{{ title }}</h3>
          </div>
          <button class="close-button" type="button" aria-label="Close create form" @click="emit('close')">×</button>
        </header>
        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
        <label>
          <span>Code</span>
          <input v-model="form.code" type="text" placeholder="Code">
        </label>
        <label>
          <span>Name</span>
          <input v-model="form.name" type="text" placeholder="Name">
        </label>
        <label v-if="type === 'equipment'">
          <span>{{ baseAmountLabel || 'Base amount' }}</span>
          <input v-model="form.base_amount" min="0.000001" step="0.000001" type="number" placeholder="168">
        </label>
        <footer>
          <button class="secondary-button" type="button" @click="emit('close')">Cancel</button>
          <button type="button" :disabled="saving || !form.code.trim() || !form.name.trim()" @click="submit">
            {{ saving ? 'Saving...' : 'Create' }}
          </button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.modal-backdrop { position: fixed; z-index: 1100; inset: 0; display: grid; padding: 24px; background: rgb(5 31 67 / 52%); place-items: center; }
.create-modal { display: grid; width: min(460px, 100%); gap: 12px; padding: 20px; border: 1px solid #cbd7e5; background: #fff; box-shadow: 0 24px 60px rgb(5 31 67 / 25%); }
header, footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.eyebrow { margin: 0 0 4px; color: #66726e; font-size: 11px; font-weight: 800; text-transform: uppercase; }
h3, p { margin: 0; }
label { display: grid; gap: 6px; color: #53625d; font-size: 13px; font-weight: 700; }
input { padding: 11px 12px; border: 1px solid #aebdcc; font: inherit; }
.close-button { padding: 2px 10px; color: #66726e; background: transparent; font-size: 28px; }
.secondary-button, footer button:last-child { padding: 8px 12px; border: 1px solid #b9c5c1; font: inherit; font-weight: 700; cursor: pointer; }
.secondary-button { color: #285b9a; background: #fff; }
footer button:last-child { color: #fff; background: #285b9a; }
.error-message { color: #852d22; }
</style>
